<template>
  <div id="acc_list_main_map" ref="listMap">
    <div ref="mapTop"></div>


    <LazyPartsUniMap ref="mapContent" :events="{load: onMapLoad, move: onMoveChange}"/>

    <div class="d-none" v-if="!isPhoneDevice">
      <PopupItem :id="popupItemId" ref="popupItemRef" :activeLocale="activeLocale" :activeCountry="activeCountry"/>
    </div>
  </div>
</template>

<script setup lang="ts">
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import {computed, onMounted, onUnmounted, PropType, ref, watch} from "vue";
import PopupItem from "./acc_list_main_map_popup_item";
import {useNuxtApp, useRuntimeConfig} from "#app";
import {clearObserverObj, getDistance} from "../../../../../lib/functions";
import useStateAccommodationList from "../../../../../composables/state/accommodation_list";
import {GATracker} from "../../../../../composables/ga_tracker";


const props = defineProps({
  mapObjects: Object as PropType<Array<{ latitude: number, longitude: number, id: number }>>,
  totalCount: Number,
  activeLocale: Object as PropType<{ code: string }>,
  activeCountry: Object as PropType<{ code: string }>
});

const emit = defineEmits(['onBoundsChange', 'onCenterChange']);

const {$tyrionApi, $isDesktop} = useNuxtApp();
const isPhoneDevice = computed(() => {
  return !$isDesktop();
});


const icon = new L.Icon({
  iconUrl: '/img/map-marker.png',
  iconSize: [27, 45],
})

const popupItemId = ref<number>(-1);
const listMap = ref(null);
const loaded = ref(false);
const initZoomChange = ref(false);
const mapContent = ref(null);
const mapMarkers = ref(null);
const popupItemRef = ref(null);
const changeMovementTimeout = ref<null | number>(null);
const previousPosition = ref({lat: 0, lng: 0, zoom: 8});

const {selectedMapItem} = useStateAccommodationList();
const {GATrackEvent} = GATracker();

watch(() => props.mapObjects, (val) => {
  updateMapMarkers(val);
});

watch(() => previousPosition.value, (val, prevValue) => {
  if (val.zoom != prevValue.zoom && initZoomChange.value) {

    GATrackEvent({
      event: 'map_zoom_main_accommodations',
      action: 'zoom',
      label: 'Move map on a main accommodations list',
      language: props.activeLocale.code,
      value: val.zoom
    }, 0);

  }
});

onUnmounted(() => {
  window.removeEventListener("resize", updatePhoneMapSize);
});

onMounted(() => {
  if (isPhoneDevice.value) {
    setTimeout(() => {
      updatePhoneMapSize();
    }, 600);
  }
  setTimeout(() => {
    initZoomChange.value = true;
  }, 2000);
});

const onMapLoad = () => {
  setTimeout(() => {
    mapMarkers.value = L.markerClusterGroup();
    loaded.value = true;
    updateMap();
  }, 300);
};

const updateMapLocation = (location, zoom = undefined) => {
  mapContent.value.map.setView([location.latitude, location.longitude], zoom ? Number(zoom) : undefined);
}

const updatePhoneMapSize = () => {
  if (listMap.value) {
    listMap.value.style.setProperty('--vh', `${window.innerHeight / 100}px`);
  }
};

const updateMap = () => {
  const updateFunc = (mapObject) => {
    const center = mapObject.getCenter();
    if (getDistance([previousPosition.value.lat, previousPosition.value.lng], [center.lat, center.lng]) > 1500 || Math.round(previousPosition.value.zoom) != Math.round(mapObject.getZoom())) {
      emit('onBoundsChange', mapObject.getBounds());
      emit('onCenterChange', {
        lat: center.lat,
        lng: center.lng,
        zoom: mapObject.getZoom()
      });
      previousPosition.value = {lat: center.lat, lng: center.lng, zoom: mapObject.getZoom()};
    }

  }

  updateFunc(mapContent.value.map);
}

const onMoveChange = () => {
  if (changeMovementTimeout.value !== null) clearTimeout(changeMovementTimeout.value);
  changeMovementTimeout.value = setTimeout(() => {
    updateMap();

  }, 400);
}

const updateMapMarkers = (mapObjects) => {


  const objects = clearObserverObj(mapObjects);

  if (mapMarkers.value) {
    mapMarkers.value.eachLayer((c) => {
      let existing = false;
      objects.forEach((mItem) => {
        if (c.options.accommodation_id === mItem.id) {
          existing = true;
        }
      });
      if (existing === false) {
        mapMarkers.value.removeLayer(c);
      }
    });
  }


  objects.forEach((mItem) => {

    let existing = false;
    mapMarkers.value.eachLayer((c) => {
      if (c.options.accommodation_id === mItem.id) {
        existing = true;
      }
    });
    if (!existing) {
      const marker = L.marker([mItem.latitude, mItem.longitude], {icon: icon, accommodation_id: mItem.id});
      marker.bindPopup("Loading", {
        minWidth: 150
      });
      marker.on("click", () => {
        afterMarkerClick(marker);
      });
      marker.on("popupclose", () => {
        afterMarkerClose(marker);
      });
      mapMarkers.value.addLayer(marker);
    }
  })

  mapContent.value.map.addLayer(mapMarkers.value);

}

const afterMarkerClick = (marker) => {
  popupItemId.value = marker.options.accommodation_id;
  if (!isPhoneDevice.value) {
    marker.getPopup().setContent(popupItemRef.value.$el);
  }
  selectedMapItem.value = popupItemId.value;
  updateMapLocation({latitude: marker.getLatLng().lat, longitude: marker.getLatLng().lng});

  GATrackEvent({
    event: 'map_marker_popup_accommodation',
    action: 'click',
    value: popupItemId.value,
    language: props.activeLocale.code,
    label: 'Click on a map marker'
  });
}

const afterMarkerClose = (marker) => {
  setTimeout(() => {
    if (marker.options.accommodation_id === selectedMapItem.value) {
      selectedMapItem.value = -1;
    }
  }, 300);
}

defineExpose({
  updateMapLocation,
  loaded
});

</script>
